/**
 * volumeNeed.js — нужен ли этой проекции объём, и какой.
 *
 * Отдельным файлом без импортов, потому что ошибка здесь не падает, а даёт
 * чёрное окно — то есть неотличима от «не загрузилось».
 *
 * Что произошло. На телефоне программа открывается в режиме одиночной стопки:
 * грузится нативная серия, объём НЕ строится, и идентификатор тома остаётся
 * пустым. Нажатие «Аксиальная» уходило дальше с пустым идентификатором, ветка
 * построения объёма (`else if (volumeId)`) не выполнялась вовсе — и человек
 * видел чёрный экран. Без ошибки, без сообщения, без полосы загрузки.
 *
 * Стоило нажать MPR — всё появлялось: та ветка объём как раз создаёт. И
 * появлялось быстро, потому что кадры уже лежали в кэше после загрузки
 * стопки; заново качать было нечего, оставалось собрать объём.
 *
 * Отсюда правило: у проекции, которой нужен объём, идентификатор берётся из
 * текущей серии, даже если объём ещё не строили.
 */

/**
 * Режимы, которые читают ОБЪЁМ.
 *
 * '2d' — исходная стопка, читается как есть. Остальные — реформаты: без
 * собранного объёма показывать им нечего.
 */
export const VOLUME_MODES = Object.freeze(['axial', 'sagittal', 'coronal', '3d']);

export function modeNeedsVolume(mode) {
  return VOLUME_MODES.includes(String(mode || ''));
}

/**
 * @param {object} p
 * @param {string} p.mode режим, который открывают
 * @param {string|null} [p.currentVolumeId] уже известный идентификатор тома
 * @param {string|null} [p.seriesUID] текущая серия
 * @returns {{needsVolume: boolean, volumeId: string|null, derived: boolean, reason: string}}
 */
export function planViewModeVolume({ mode, currentVolumeId = null, seriesUID = null }) {
  const needsVolume = modeNeedsVolume(mode);

  if (!needsVolume) {
    return { needsVolume: false, volumeId: currentVolumeId || null, derived: false, reason: 'стопка читается без объёма' };
  }
  if (currentVolumeId) {
    // Известному идентификатору верить можно, только если он ОТ ЭТОЙ серии.
    //
    // Ситуация из жизни: открыли серию A, построили объём, вернулись в стопку
    // и выбрали серию B. Идентификатор тома при этом никто не очищает — он
    // так и показывает на volume_A. Нажатие «Аксиальная» со старым правилом
    // уходило со словами «объём уже известен», и человеку показывали
    // реформаты ПРЕДЫДУЩЕГО исследования. Для диагностической программы это
    // худший род ошибки: неправильная картинка без признаков неправильности.
    const short = String(currentVolumeId).replace(/^cornerstoneStreamingImageVolume:/, '');
    const expected = seriesUID ? `volume_${seriesUID}` : null;
    if (!expected || short === expected) {
      return { needsVolume: true, volumeId: currentVolumeId, derived: false, reason: 'объём уже известен' };
    }
    return {
      needsVolume: true,
      volumeId: expected,
      derived: true,
      reason: 'известный объём от другой серии — идентификатор выведен заново',
    };
  }
  if (!seriesUID) {
    return { needsVolume: true, volumeId: null, derived: false, reason: 'серия не выбрана — строить нечего' };
  }
  return {
    needsVolume: true,
    volumeId: `volume_${seriesUID}`,
    derived: true,
    reason: 'идентификатор выведен из серии — объём построится по требованию',
  };
}

export default { VOLUME_MODES, modeNeedsVolume, planViewModeVolume };
